import {Component, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {Router} from '@angular/router';
import {CarBrand, CarType, DamageCase, DamageCaseGridVm, Insurance, JobType, Receiver} from '../../../models/models';
import {DamageInternalStateLabels} from 'src/app/models/enums';
import {LoginService} from '../../../services/login.service';
import {CheckService} from '../../../services/check.service';
import {DamageAdministrationService} from '../../../services/damage-administration.service';
import {GridComponent, RowDataBoundEventArgs, FilterService } from '@syncfusion/ej2-angular-grids';
import {ClickEventArgs} from '@syncfusion/ej2-angular-navigations';
import {forkJoin} from 'rxjs';

@Component({
  selector: 'app-damage-administration-case-overview.component',
  templateUrl: './damage-administration-case-overview.component.html',
  styleUrls: ['./damage-administration-case-overview.component.css'],
  providers: [FilterService]
})
export class DamageAdministrationCaseOverview implements OnInit {
  
  @ViewChild('feedStateTemplate', { static: true }) feedStateTemplate!: TemplateRef<any>;
  @ViewChild('damageAdministrationOverviewGrid', {static: true}) private damageAdministrationOverviewGrid: GridComponent;

  public locale: string;
  public dataSource: DamageCase[] = [];
  public formatOptions: any;
  public toolbar: any[] = ['ExcelExport', 'PdfExport', { text: 'Neu laden', tooltipText: 'Daten neu laden', prefixIcon: 'e-refresh', id: 'reload' }];

  constructor(
    private loginService: LoginService,
    private checkService: CheckService,
    private damageAdministrationService: DamageAdministrationService,
    private router: Router,
  ) { }

  ngOnInit(): void {
    window.scrollTo(0, 0);
    this.loginService.checkToken();
    this.checkService.checkRight(12);
    this.loadDataSource();
  }

  loadDataSource() {
    this.damageAdministrationOverviewGrid.showSpinner();
    forkJoin([
      this.damageAdministrationService.getReceivers(),
      this.damageAdministrationService.getCarTypes(),
      this.damageAdministrationService.getJobTypes(),
      this.damageAdministrationService.getEmployees(),
      this.damageAdministrationService.getDamageCases(),
      this.damageAdministrationService.getCarBrands(),
      this.damageAdministrationService.getInsurances(),
    ]).subscribe(([receiverResponse, carTypeResponse, jobTypeResponse, employeeResponse, caseResponse, carBrandResponse, insuranceResponse]) => {
      if (!caseResponse.Ok || !employeeResponse.Ok || !carBrandResponse.Ok || !carTypeResponse.Ok || !jobTypeResponse.Ok || !receiverResponse.Ok || !insuranceResponse.Ok) return;

      // Lookup Maps
      const receiverMap = this.toMap<Receiver>(receiverResponse.Obj, 'ReceiverId', 'ReceiverName');
      const carTypeMap = this.toMap<CarType>(carTypeResponse.Obj, 'CarTypeId', 'CarTypeName');
      const jobTypeMap = this.toMap<JobType>(jobTypeResponse.Obj, 'JobTypeId', 'JobTypeName');
      const carBrandMap = this.toMap<CarBrand>(carBrandResponse.Obj, 'CarBrandId', 'CarBrandName');
      const employeeMap = new Map<number, string>(employeeResponse.Obj.map(e => [e.UniqueId, `${e.FirstName} ${e.LastName}`]));
      const insuranceMap = this.toMap<Insurance>(insuranceResponse.Obj, 'InsuranceId', 'InsuranceName')
      
      // Generate ViewModel
      const gridData: DamageCaseGridVm[] = caseResponse.Obj.map(dc => ({
        ...dc,
        ReceiverName: receiverMap.get(dc.ReceiverId) ?? '',
        WorkshopName: receiverMap.get(dc.WorkshopId) ?? '',
        JobTypeName: jobTypeMap.get(dc.JobTypeId) ?? '',
        CarBrandName: carBrandMap.get(dc.BrandId) ?? '',
        CarTypeName: carTypeMap.get(dc.CarTypeId) ?? '',
        CausedByName: employeeMap.get(dc.CausedById) ?? '',
        InternalStateName: DamageInternalStateLabels[dc.InternalState] ?? '',
        InsuranceName: insuranceMap.get(dc.InsuranceId) ?? ''
      }));

      this.dataSource = gridData;
      this.initGrid();
    });
  }

  private toMap<T>(array: T[], idKey: keyof T, valueKey: keyof T): Map<number, string> {
    return new Map(array.map(x => [x[idKey] as unknown as number, x[valueKey] as unknown as string]));
  }

  initGrid() {
    this.damageAdministrationOverviewGrid.allowExcelExport = true;
    this.damageAdministrationOverviewGrid.allowPdfExport = true;
    this.damageAdministrationOverviewGrid.allowPaging = true;
    this.damageAdministrationOverviewGrid.allowFiltering = true;
    this.damageAdministrationOverviewGrid.filterSettings = {
      type: 'Excel',
      operators: {
        stringOperator: [
          { value: 'contains', text: 'Contains' },
          { value: 'startsWith', text: 'Starts With' },
          { value: 'endsWith', text: 'Ends With' }
        ]
      }
    };
    this.damageAdministrationOverviewGrid.pageSettings = {pageCount: 5, pageSize: 15};
    this.damageAdministrationOverviewGrid.toolbar = ['ExcelExport', 'PdfExport', 'Search'];
    this.damageAdministrationOverviewGrid.editSettings = {allowEditing: false, allowAdding: false, allowDeleting: false, mode: 'Normal'};
    this.damageAdministrationOverviewGrid.hideSpinner();
  }

  rowDataBound(args: RowDataBoundEventArgs) {
    this.damageAdministrationOverviewGrid.toolbar = [
      'ExcelExport',
      'PdfExport',
      { text: 'Neu laden', tooltipText: 'Daten neu laden', prefixIcon: 'e-refresh', id: 'reload' }
    ];
  }

  onRecordDoubleClick($event: MouseEvent) {
    // @ts-ignore
    this.router.navigate(['/damageAdministrationCase', $event.rowData.Id]);
  }

  toolbarClick($event: ClickEventArgs) {
    if ($event.item.id === 'damageAdministrationOverviewGrid_excelexport') {
      this.damageAdministrationOverviewGrid.excelExport();
    }
    if ($event.item.id === 'damageAdministrationOverviewGrid_pdfexport') {
      this.damageAdministrationOverviewGrid.pdfExport();
    }
    if ($event.item.id === 'reload') {
      this.reloadGrid();
    }
  }

  reloadGrid() {
    this.damageAdministrationOverviewGrid.showSpinner();
    this.loadDataSource();
  }
}
